import { EstateRagDocumentObject } from '@/modules/ai/models/EstateRagDocumentObject';
import { EstateRagSimilarityDataObject } from '@/modules/ai/models/EstateRagSimilarityDataObject';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { EstateRagSimilarityDataMetadataObject } from '@/modules/ai/models/EstateRagSimilarityDataMetadataObject';
import { EstateRagFeedbackObject } from '@/modules/ai/models/EstateRagFeedbackObject';
import { ClaraEscalationObject } from '@/modules/ai/models/ClaraEscalationObject';
import Lang from '@/modules/core/app/services/Lang';
import alertCircle from '@/assets/illustrations/alert-circle.svg';
import alertDiamond from '@/assets/illustrations/alert-diamond.svg';
import { ClaraMessageStatusType } from '@/modules/ai/constants/ClaraMessageStatusType';
import { MessageActor } from '@/modules/ai/constants/MessageActor';
import { ClaraEscalationBranch } from '@/modules/ai/constants/ClaraEscalationBranch';

export class ClaraMessageObject extends BaseEntityModel {
  id?: number;
  conversationId?: number;
  identifier?: string;
  text?: string;
  actor?: string;
  status?: ClaraMessageStatusType;
  isOutdated?: boolean;
  modelName?: string;
  relatedQuestions?: string[] | null;
  isInLibrary?: boolean;
  documents?: Relationship<EstateRagDocumentObject[] | null>;
  similarityData?: Relationship<EstateRagSimilarityDataObject[] | null>;
  feedback?: Relationship<EstateRagFeedbackObject | null>;
  actions?: string[] | null;
  escalation?: Relationship<ClaraEscalationObject | null>;
  isNew?: boolean;

  constructor(model: Partial<ClaraMessageObject> = {}) {
    super(model);

    this.id = model.id;
    this.conversationId = model.conversationId;
    this.identifier = model.identifier;
    this.text = model.text;
    this.actor = model.actor;
    this.status = model.status;
    this.isOutdated = model.isOutdated;
    this.modelName = model.modelName;
    this.relatedQuestions = model.relatedQuestions;
    this.isInLibrary = model.isInLibrary;
    this.documents = EstateRagDocumentObject.mapModelsOrNull(model.documents);
    this.similarityData = EstateRagSimilarityDataObject.mapModelsOrNull(
      model.similarityData?.reverse()
    );
    this.feedback = EstateRagFeedbackObject.mapModelOrNull(model.feedback);
    this.actions = model.actions ?? null;
    this.escalation = ClaraEscalationObject.mapModelOrNull(model.escalation);
    this.isNew = model.isNew ?? false;
  }

  getDocument(
    similarityDataMetadata: EstateRagSimilarityDataMetadataObject
  ): EstateRagDocumentObject | undefined {
    return this.documents?.find(
      (doc: EstateRagDocumentObject) =>
        doc.ragDocumentId === similarityDataMetadata.documentId &&
        doc.source === similarityDataMetadata.source
    );
  }

  get isUserMessage(): boolean {
    return this.actor === MessageActor.USER;
  }

  get isClaraMessage(): boolean {
    return this.actor === MessageActor.CLARA;
  }

  get isSuccess(): boolean {
    return this.status === ClaraMessageStatusType.SUCCESS;
  }

  get isNoMatch(): boolean {
    return this.status === ClaraMessageStatusType.NO_MATCH;
  }

  get isNoRelevance(): boolean {
    return this.status === ClaraMessageStatusType.NO_RELEVANCE;
  }

  get isFailed(): boolean {
    return this.status === ClaraMessageStatusType.FAILED;
  }

  get isClarification(): boolean {
    return this.status === ClaraMessageStatusType.CLARIFICATION;
  }

  get isUpgradeEscalation(): boolean {
    return this.escalation?.branch === ClaraEscalationBranch.UPGRADE;
  }

  get canRetrySystemError(): boolean {
    return (
      this.escalation?.branch === ClaraEscalationBranch.SYSTEM_ERROR &&
      this.escalation.createsTicket !== true
    );
  }

  get createsSupportTicket(): boolean {
    return this.escalation?.createsTicket === true;
  }

  get hasNonSuccessStatus(): boolean {
    if (!this.status) {
      return false;
    }

    return [
      ClaraMessageStatusType.NO_MATCH,
      ClaraMessageStatusType.NO_RELEVANCE,
      ClaraMessageStatusType.FAILED
    ].includes(this.status);
  }

  get isSavedInHistory(): boolean {
    return this.isSuccess || this.isNoMatch || this.isClarification;
  }

  get hasAlert(): boolean {
    return !this.text || this.isNoMatch || !!this.isOutdated;
  }

  get alertMessage(): string {
    if (!this.text || this.isNoMatch) {
      return Lang.t('ai.no-answer-found');
    }

    if (this.isOutdated) {
      return Lang.t('ai.outdated-refresh');
    }

    return '';
  }

  get alertIcon(): string {
    if (!this.text || this.isNoMatch) {
      return alertCircle;
    }

    if (this.isOutdated) {
      return alertDiamond;
    }

    return '';
  }
}
